import { createTheme } from '@mui/material';
import { colorTokens, themeTokens } from './tokens';
import type {} from './types';

const {
  content,
  surface,
  border,
  shadow,
  icon,
  status,
  accent,
  chart,
  identity,
  code,
  neutral,
} = colorTokens;
const { fontFamily, fontWeight, components } = themeTokens;
const {
  typography,
  formControl,
  inputLabel,
  input,
  menu,
  autocomplete,
  dialog,
  drawer,
  divider,
  tooltip,
  alert,
} = components;

const theme = createTheme({
  palette: {
    common: {
      black: content.primary,
      white: content.inverse,
    },
    primary: {
      main: accent.primary,
      dark: accent.primaryStrong,
    },
    secondary: {
      main: accent.secondary,
    },
    warning: {
      main: status.warning.main,
    },
    error: {
      main: status.error.main,
    },
    info: {
      main: status.info.main,
    },
    success: {
      main: status.success.main,
    },
    grey: neutral,
    text: content.translucent,
    divider: border.translucent,
    background: {
      default: surface.base,
      paper: surface.base,
    },
    action: {
      active: icon.translucent.active,
      hover: surface.translucent.hover,
      selected: surface.translucent.selected,
      disabled: icon.translucent.disabled,
      disabledBackground: surface.translucent.disabled,
      focus: surface.translucent.focus,
    },
    content,
    surface,
    border,
    shadow,
    icon,
    status,
    accent,
    chart,
    identity,
    code,
  },

  tokens: themeTokens,

  typography: {
    fontFamily: fontFamily.sans,
    fontWeightMedium: fontWeight.medium,
    fontWeightBold: fontWeight.semibold,
  },

  components: {
    MuiTypography: {
      defaultProps: {
        color: typography.color,
      },
      styleOverrides: {
        root: {
          fontSize: typography.fontSize,
        },
      },
      variants: [
        {
          props: { variant: 'h3' },
          style: {
            fontWeight: typography.headingWeight,
            fontSize: typography.headingSize.h3,
          },
        },
        {
          props: { variant: 'h4' },
          style: {
            fontWeight: typography.headingWeight,
            fontSize: typography.headingSize.h4,
          },
        },
        {
          props: { variant: 'h5' },
          style: {
            fontWeight: typography.headingWeight,
            fontSize: typography.headingSize.h5,
          },
        },
        {
          props: { variant: 'h6' },
          style: {
            fontWeight: typography.headingWeight,
            fontSize: typography.headingSize.h6,
          },
        },
        {
          props: { variant: 'body1' },
          style: {
            fontWeight: typography.body1.fontWeight,
            fontSize: typography.body1.fontSize,
            color: typography.body1.color,
          },
        },
        {
          props: { variant: 'caption' },
          style: {
            fontSize: typography.caption.fontSize,
            color: `${typography.caption.color} !important`,
            lineHeight: typography.caption.lineHeight,
          },
        },
      ],
    },

    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },

    MuiFormControl: {
      styleOverrides: {
        root: {
          '.MuiInputLabel-root': {
            paddingRight: formControl.labelPaddingRight,
            paddingTop: formControl.labelPaddingTop,
            color: `${formControl.label} !important`,
            fontSize: formControl.labelFontSize,
          },
          '.MuiInputLabel-shrink': {
            fontSize: 'unset',
            paddingTop: 'unset',
          },
          '.Mui-focused': {
            color: `${formControl.labelFocused} !important`,
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: inputLabel.color,
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          boxShadow: menu.shadow,
          border: `1px solid ${menu.border}`,
          borderRadius: menu.radius,
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: dialog.radius,
          border: `1px solid ${dialog.border}`,
        },
        root: {
          '& .MuiBackdrop-root': {
            backgroundColor: dialog.backdrop,
          },
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: divider.color,
        },
      },
      variants: [
        {
          props: { light: true },
          style: {
            borderColor: divider.lightColor,
          },
        },
      ],
    },

    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          border: `1px solid ${autocomplete.border}`,
          boxShadow: autocomplete.shadow,
          borderRadius: autocomplete.radius,
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: `${input.radius} !important`,
        },
      },
    },

    MuiTooltip: {
      defaultProps: {
        placement: 'bottom-start',
      },
      styleOverrides: {
        tooltip: {
          background: tooltip.bg,
          borderRadius: tooltip.radius,
          padding: tooltip.padding,
          fontSize: tooltip.fontSize,
          fontWeight: tooltip.fontWeight,
          // `width` and not `min-width`: a min-width of fit-content beats
          // max-width in CSS, which is what used to let tooltips grow past
          // the limit.
          width: 'fit-content',
          maxWidth: tooltip.maxWidth,
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: alert.radius,
          alignItems: 'center',
        },
        standardSuccess: {
          backgroundColor: alert.successBg,
          color: alert.fg,
        },
        standardError: {
          backgroundColor: alert.errorBg,
          color: alert.fg,
        },
        standardWarning: {
          backgroundColor: alert.warningBg,
          color: alert.fg,
        },
        filledWarning: {
          backgroundColor: alert.attentionBg,
          color: alert.fg,
        },
        standardInfo: {
          color: alert.fg,
          backgroundColor: alert.infoBg,
        },
      },
      defaultProps: { severity: 'info' },
    },
  },

  mixins: {
    drawerFooter: {
      height: drawer.footerHeight,
      minHeight: drawer.footerHeight,
    },
  },
});

export default theme;
